import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, GeoJSON } from 'react-leaflet';
import { ArrowLeft, CircleHelp, Volume2, VolumeX, Pause, Play, Square, MapPin, MapPinOff, Gauge, RotateCcw } from 'lucide-react';
import { LocationInput } from '../types';
import { OSDORP_BOUNDS, OSDORP_CENTER } from '../config/osdorpArea';
import RouteFeedbackModal from '../components/RouteFeedbackModal';
import MapExplainerModal from '../components/MapExplainerModal';
import { useTranslation } from '../hooks/useTranslation';
import { 
  generateNavigationInstructions, 
  getCurrentInstruction
} from '../utils/navigationInstructions';
import { backendApi } from '../services/backendApi';
import { localStorageUtils } from '../utils/localStorage';
import 'leaflet/dist/leaflet.css';
import wijkGeoJson from '../data/json/wijk_geojson_osdpm.json';

// Fix for default markers in react-leaflet
import L from 'leaflet';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface NavigationModeProps {
  startLocation: LocationInput;
  endLocation: LocationInput;
  routeCoordinates: [number, number][];
  requestId?: number; // Request ID from backend route init
  onBack: () => void;
  onStopNavigation: () => void;
  onRouteCompleted?: () => void;
}

interface NavigationInstruction {
  instruction: string;
  distance: number;
  type: 'turn-left' | 'turn-right' | 'straight' | 'arrive' | 'start';
}

function haversineDistanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function remainingDistanceAlongRoute(
  currentPos: [number, number],
  routeCoords: [number, number][],
  endPos: [number, number]
): number {
  if (routeCoords.length < 2) {
    return haversineDistanceMeters(currentPos[0], currentPos[1], endPos[0], endPos[1]);
  }

  let closestIndex = 0;
  let minDistance = Infinity;
  for (let i = 0; i < routeCoords.length; i++) {
    const [routeLat, routeLng] = routeCoords[i];
    const dist = haversineDistanceMeters(currentPos[0], currentPos[1], routeLat, routeLng);
    if (dist < minDistance) {
      minDistance = dist;
      closestIndex = i;
    }
  }

  let remainingDistance = 0;
  for (let i = closestIndex; i < routeCoords.length - 1; i++) {
    const [currLat, currLng] = routeCoords[i];
    const [nextLat, nextLng] = routeCoords[i + 1];
    remainingDistance += haversineDistanceMeters(currLat, currLng, nextLat, nextLng);
  }

  const [lastRouteLat, lastRouteLng] = routeCoords[routeCoords.length - 1];
  remainingDistance += haversineDistanceMeters(lastRouteLat, lastRouteLng, endPos[0], endPos[1]);

  return remainingDistance;
}

const NavigationMode: React.FC<NavigationModeProps> = ({
  startLocation,
  endLocation,
  routeCoordinates,
  requestId,
  onBack,
  onStopNavigation,
  onRouteCompleted
}) => {
  const { t, language } = useTranslation();
  const [currentPosition, setCurrentPosition] = useState<[number, number] | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  
  // Mock geolocation state (development mode only)
  const [isMockEnabled, setIsMockEnabled] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(1.4); // m/s
  const [showMockControls, setShowMockControls] = useState(false);

  // Check mock status on mount and when route changes
  useEffect(() => {
    const checkMockStatus = () => {
      const mockEnabled = localStorage.getItem('mock-geolocation');
      setIsMockEnabled(!!mockEnabled);
    };
    
    // Check on mount
    checkMockStatus();
    
    // Listen for storage changes (when mock is enabled/disabled from console)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'mock-geolocation') {
        checkMockStatus();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically (for same-tab changes)
    const interval = setInterval(checkMockStatus, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Expose route coordinates to window and reset simulation when route changes
  useEffect(() => {
    if (typeof window !== 'undefined' && routeCoordinates.length > 0) {
      (window as any).__navigationRouteCoordinates = routeCoordinates;
      
      // Reset any ongoing simulation to start of new route
      if (typeof (window as any).resetRouteSimulation === 'function') {
        (window as any).resetRouteSimulation(routeCoordinates);
        setIsSimulating(false); // Reset simulation state
      }

      console.log('%c📍 Route coordinates available:', 'color: #4CAF50; font-weight: bold;');
      console.log('  window.__navigationRouteCoordinates');
      console.log('%c💡 To simulate walking along the route:', 'color: #2196F3; font-weight: bold;');
      console.log('  window.simulateRouteWalk(window.__navigationRouteCoordinates)');
      console.log('  window.simulateRouteWalk(window.__navigationRouteCoordinates, 2.0) // 2x speed');
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__navigationRouteCoordinates;
      }
    };
  }, [routeCoordinates]);

  const [currentInstruction, setCurrentInstruction] = useState<NavigationInstruction>({
    instruction: t('navigation.start'),
    distance: 0,
    type: 'straight'
  });
  const [routeProgress, setRouteProgress] = useState({
    totalDistance: 0, // Will be calculated from route
    remainingDistance: 0, // meters
    totalDuration: 0, // minutes
    remainingDuration: 0, // minutes
    progressPercentage: 0 // percentage
  });
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showMapExplainer, setShowMapExplainer] = useState(false);
  const [isRouteCompleted, setIsRouteCompleted] = useState(false);
  const [hasStartedNavigation, setHasStartedNavigation] = useState(false);
  
  // Use ref to track previous instruction to avoid unnecessary updates
  const previousInstructionRef = useRef<string | null>(null);

  // Memoized icon for the current location marker to prevent flickering
  const currentLocationIcon = useMemo(
    () =>
      new L.DivIcon({
        className: 'current-location-marker',
        html: '<div class="red-dot"></div>',
        iconSize: [12, 12],
        iconAnchor: [6, 6]
      }),
    []
  );

  // Calculate total route distance from coordinates
  const totalRouteDistance = useMemo(() => {
    if (routeCoordinates.length < 2) return 0;

    let total = 0;
    for (let i = 1; i < routeCoordinates.length; i++) {
      const [prevLat, prevLng] = routeCoordinates[i - 1];
      const [currLat, currLng] = routeCoordinates[i];
      total += haversineDistanceMeters(prevLat, prevLng, currLat, currLng);
    }
    return Math.round(total);
  }, [routeCoordinates]);
  
  // Reset navigation state when route changes
  useEffect(() => {
    setHasStartedNavigation(false);
    setIsRouteCompleted(false);
    setShowFeedbackModal(false);
  }, [routeCoordinates]);
  
  // Mark navigation as started when we get a valid position and we're not already at the destination
  useEffect(() => {
    if (currentPosition && routeCoordinates.length > 0 && !hasStartedNavigation && endLocation.coordinates) {
      // Check if we're not already at the destination (within 50m)
      const endPos: [number, number] = [
        endLocation.coordinates.lat,
        endLocation.coordinates.lng
      ];
      const distanceToEnd = remainingDistanceAlongRoute(
        currentPosition,
        routeCoordinates,
        endPos
      );
      // Only mark as started if we're not already at the destination
      if (distanceToEnd >= 50) {
        setHasStartedNavigation(true);
      }
    }
  }, [currentPosition, routeCoordinates, endLocation.coordinates, hasStartedNavigation]);

  // Generate dynamic navigation instructions from route coordinates
  const navigationInstructions = useMemo(() => {
    if (routeCoordinates.length < 2 || !endLocation.coordinates) {
      return [];
    }
    
    const endPos: [number, number] = [
      endLocation.coordinates.lat,
      endLocation.coordinates.lng
    ];
    
    return generateNavigationInstructions(
      routeCoordinates,
      currentPosition,
      endPos
    );
  }, [routeCoordinates, currentPosition, endLocation.coordinates]);

  // Update current instruction based on position and route
  useEffect(() => {
    if (navigationInstructions.length === 0) {
      // Fallback to start instruction
      const startInstruction = t('navigation.start');
      if (previousInstructionRef.current !== startInstruction) {
        setCurrentInstruction({
          instruction: startInstruction,
          distance: 0,
          type: 'start'
        });
        previousInstructionRef.current = startInstruction;
      }
      return;
    }

    if (!endLocation.coordinates) return;

    const endPos: [number, number] = [
      endLocation.coordinates.lat,
      endLocation.coordinates.lng
    ];

    let nextInstruction = getCurrentInstruction(
      navigationInstructions,
      currentPosition,
      endPos
    );

    if (nextInstruction) {
      // If navigation hasn't started yet, never show an "arrive" instruction,
      // always treat it as a "start" instruction instead.
      if (!hasStartedNavigation && nextInstruction.type === 'arrive') {
        nextInstruction = {
          ...nextInstruction,
          instruction: 'start',
          type: 'start',
        };
      }

      // Map instruction type to translation key
      let instructionText: string;
      switch (nextInstruction.instruction) {
        case 'start':
          instructionText = t('navigation.start');
          break;
        case 'turnLeft':
          instructionText = t('navigation.turnLeft');
          break;
        case 'turnRight':
          instructionText = t('navigation.turnRight');
          break;
        case 'goStraight':
          instructionText = t('navigation.goStraight');
          break;
        case 'arrived':
          instructionText = t('navigation.arrived');
          break;
        default:
          instructionText = t('navigation.goStraight');
      }

      // Create a unique key for this instruction to compare
      const instructionKey = `${nextInstruction.type}-${nextInstruction.distance}-${instructionText}`;
      
      // Only update if instruction actually changed
      if (previousInstructionRef.current !== instructionKey) {
        setCurrentInstruction({
          instruction: instructionText,
          distance: nextInstruction.distance,
          type: nextInstruction.type
        });
        previousInstructionRef.current = instructionKey;
      }
    }
  }, [navigationInstructions, currentPosition, endLocation.coordinates, language, hasStartedNavigation, t]);

  // Get current GPS position (real or mock, depending on geolocationMock state)
  useEffect(() => {
    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setCurrentPosition([position.coords.latitude, position.coords.longitude]);
      },
      (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Geolocation error (non‑blocking):', error);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 1000
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, [isMockEnabled]);

  // Initialize total distance when route coordinates are available
  useEffect(() => {
    if (totalRouteDistance > 0) {
      setRouteProgress(prev => ({
        ...prev,
        totalDistance: totalRouteDistance,
        totalDuration: Math.round(totalRouteDistance / 1.4 / 60), // Walking speed ~1.4 m/s
        // Initialize progress to 0% if no current position yet
        progressPercentage: currentPosition ? prev.progressPercentage : 0,
        remainingDistance: currentPosition ? prev.remainingDistance : totalRouteDistance
      }));
    }
  }, [totalRouteDistance, currentPosition]);

  // Update route progress based on current position
  useEffect(() => {
    if (currentPosition && endLocation.coordinates && totalRouteDistance > 0 && routeCoordinates.length > 0) {
      // Calculate remaining distance along the route path (not straight line)
      const endPos: [number, number] = [
        endLocation.coordinates.lat,
        endLocation.coordinates.lng
      ];
      
      const rawRemainingDistance = remainingDistanceAlongRoute(
        currentPosition,
        routeCoordinates,
        endPos
      );

      // Straight-line distance from user to destination
      const directDistanceToEnd = haversineDistanceMeters(
        currentPosition[0],
        currentPosition[1],
        endPos[0],
        endPos[1]
      );

      // For the UI "remaining distance", show whichever is larger:
      // - distance along the route, or
      // - direct distance to the destination (when user is far away/off-route).
      const displayRemainingRaw = Math.max(rawRemainingDistance, directDistanceToEnd);
      const roundedRemaining = Math.round(displayRemainingRaw);
      const remainingDuration = Math.round(roundedRemaining / 1.4 / 60); // Walking speed ~1.4 m/s
      
      setRouteProgress(prev => {
        // Ensure remaining distance does not jump backwards significantly, which can
        // cause the progress bar to flicker when the mock position is reset.
        const prevRemaining = prev.remainingDistance || totalRouteDistance;
        const maxBackwardStep = totalRouteDistance * 0.05; // allow up to 5% backward adjustment
        
        let stableRemaining = roundedRemaining;
        if (roundedRemaining > prevRemaining) {
          // Clamp backwards movement
          stableRemaining = Math.min(roundedRemaining, prevRemaining + maxBackwardStep);
        }
        
        // For progress, use only the on-route remaining distance (clamped)
        const onRouteRemaining = Math.min(rawRemainingDistance, totalRouteDistance);
        const distanceTraveled = Math.max(0, totalRouteDistance - onRouteRemaining);
        let progressPercentage = Math.max(
          0,
          Math.min(100, (distanceTraveled / totalRouteDistance) * 100)
        );
        let effectiveRemainingDuration = remainingDuration;

        // Before navigation has actually started, always show 0% progress and
        // full remaining distance, even if GPS happens to be close to the end.
        if (!hasStartedNavigation) {
          stableRemaining = totalRouteDistance;
          progressPercentage = 0;
          // ETA should reflect full route duration before navigation starts
          effectiveRemainingDuration = Math.round(totalRouteDistance / 1.4 / 60);
        }

        return {
          ...prev,
          remainingDistance: Math.round(stableRemaining),
          remainingDuration: Math.max(1, effectiveRemainingDuration),
          progressPercentage: Math.round(progressPercentage)
        };
      });

      // Check if route is completed (within 50 meters of destination)
      // Use BOTH path-based remaining distance and direct distance to destination
      // to avoid falsely marking arrival when GPS is far from the end point.
      if (
        rawRemainingDistance < 50 &&
        directDistanceToEnd < 50 &&
        !isRouteCompleted &&
        hasStartedNavigation
      ) {
        setIsRouteCompleted(true);
        setShowFeedbackModal(true);
      }
    } else if (!currentPosition && routeCoordinates.length > 0) {
      // If no current position yet, start at 0%
      setRouteProgress(prev => ({
        ...prev,
        remainingDistance: totalRouteDistance,
        remainingDuration: Math.round(totalRouteDistance / 1.4 / 60),
        progressPercentage: 0
      }));
    }
  }, [currentPosition, endLocation.coordinates, totalRouteDistance, routeCoordinates, isRouteCompleted, hasStartedNavigation]);

  // Instructions are now updated dynamically in the effect above based on position

  const handlePauseResume = () => {
    setIsPaused(!isPaused);
  };

  const handleVoiceToggle = () => {
    setVoiceEnabled(!voiceEnabled);
  };

  const handleStopNavigation = () => {
    onStopNavigation();
  };

  const handleEndNavigation = () => {
    // Show feedback modal when ending navigation
    setShowFeedbackModal(true);
  };

  const handleFeedbackSave = async (feedback: any) => {
    // Stop simulation and disable mock if enabled
    if (isSimulating) {
      handleStopSimulation();
    }
    if (isMockEnabled) {
      handleDisableMock();
    }
    
    // Send feedback to backend if we have requestId and userId
    const userId = localStorageUtils.getUserId();
    
    if (requestId && userId && feedback) {
      try {
        // Convert selectedTags array to comma-separated string
        const labels = Array.isArray(feedback.selectedTags) 
          ? feedback.selectedTags.join(',')
          : feedback.selectedTags || '';
        
        await backendApi.submitRouteFeedback(userId, requestId, {
          rating: feedback.rating || 0,
          labels: labels
        });
        
        console.log('Route feedback submitted successfully');
      } catch (error) {
        console.error('Error submitting route feedback:', error);
        // Continue even if feedback submission fails
      }
    } else {
      console.warn('Cannot submit feedback: missing requestId or userId', { requestId, userId });
    }
    
    setShowFeedbackModal(false);
    // End navigation after saving feedback
    if (onRouteCompleted) {
      onRouteCompleted();
    } else {
      onStopNavigation();
    }
  };

  const handleFeedbackSkip = () => {
    // Stop simulation and disable mock if enabled
    if (isSimulating) {
      handleStopSimulation();
    }
    if (isMockEnabled) {
      handleDisableMock();
    }
    
    setShowFeedbackModal(false);
    // End navigation after skipping feedback
    if (onRouteCompleted) {
      onRouteCompleted();
    } else {
      onStopNavigation();
    }
  };

  const handleFeedbackClose = () => {
    setShowFeedbackModal(false);
  };

  // Mock geolocation handlers (development mode only)
  const handleEnableMock = () => {
    if (typeof (window as any).enableGeolocationMock === 'function') {
      // Prefer the start of the current route as the mock start position.
      // Fallbacks: explicit startLocation coordinates, then default center (handled in util).
      const startPosFromRoute: [number, number] | undefined =
        routeCoordinates && routeCoordinates.length > 0
          ? routeCoordinates[0]
          : startLocation.coordinates
          ? [startLocation.coordinates.lat, startLocation.coordinates.lng]
          : undefined;

      (window as any).enableGeolocationMock(startPosFromRoute);

      // Also update local navigation state so the map centers correctly.
      if (startPosFromRoute) {
        setCurrentPosition(startPosFromRoute);
      }

      setIsMockEnabled(true);
    }
  };

  const handleDisableMock = () => {
    if (typeof (window as any).disableGeolocationMock === 'function') {
      (window as any).disableGeolocationMock();
      setIsMockEnabled(false);
      setIsSimulating(false);
      // Clear any simulated position so we fall back to real GPS updates
      setCurrentPosition(null);
    }
  };

  const handleStartSimulation = () => {
    if (typeof (window as any).simulateRouteWalk === 'function' && routeCoordinates.length > 0) {
      (window as any).simulateRouteWalk(routeCoordinates, simulationSpeed);
      setIsSimulating(true);
    }
  };

  const handleStopSimulation = () => {
    if (typeof (window as any).stopRouteSimulation === 'function') {
      (window as any).stopRouteSimulation();
      setIsSimulating(false);
    }
  };

  const handlePauseSimulation = () => {
    if (typeof (window as any).pauseRouteSimulation === 'function') {
      (window as any).pauseRouteSimulation();
      setIsSimulating(false);
    }
  };

  const handleResumeSimulation = () => {
    if (typeof (window as any).resumeRouteSimulation === 'function') {
      (window as any).resumeRouteSimulation();
      setIsSimulating(true);
    }
  };

  const handleResetSimulation = () => {
    if (typeof (window as any).resetRouteSimulation === 'function') {
      (window as any).resetRouteSimulation(routeCoordinates);
      setIsSimulating(false);
      // Reset position to start
      if (routeCoordinates.length > 0) {
        setCurrentPosition(routeCoordinates[0]);
      }
    }
  };

  const handleSpeedChange = (speed: number) => {
    setSimulationSpeed(speed);
    // Update speed in mock service
    if (typeof (window as any).setSimulationSpeed === 'function') {
      (window as any).setSimulationSpeed(speed);
    }
    // If a simulation is currently running, restart it with the new speed
    if (isSimulating && typeof (window as any).stopRouteSimulation === 'function' && typeof (window as any).simulateRouteWalk === 'function' && routeCoordinates.length > 0) {
      (window as any).stopRouteSimulation();
      (window as any).simulateRouteWalk(routeCoordinates, speed);
    }
  };

  const getInstructionIcon = (type: string) => {
    switch (type) {
      case 'turn-left':
        return '↰';
      case 'turn-right':
        return '↱';
      case 'straight':
      case 'start':
        return '↑';
      case 'arrive':
        return '✓';
      default:
        return '↑';
    }
  };

  const getInstructionColor = (type: string) => {
    switch (type) {
      case 'turn-left':
      case 'turn-right':
        return '#ff6b6b';
      case 'arrive':
        return '#51cf66';
      case 'start':
        return '#339af0';
      default:
        return '#339af0';
    }
  };

  // Get current date in localized format
  const getCurrentDate = () => {
    const now = new Date();
    
    if (language === 'nl') {
      const days = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
      const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
      return `${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]}`;
    } else {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return `${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]}`;
    }
  };

  return (
    <div className="navigation-mode">
      {/* Navigation Header */}
      <div className="navigation-header">
        <button className="nav-back-button" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        
        <div className="nav-destination">
          <h3>{t('navigation.navigatingTo')}</h3>
          <p>{endLocation.address}</p>
        </div>

        <div className="nav-controls">
          <button 
            className={`nav-control-btn ${voiceEnabled ? 'active' : ''}`}
            onClick={handleVoiceToggle}
            title={voiceEnabled ? t('navigation.soundOff') : t('navigation.soundOn')}
          >
            {voiceEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </button>
          
          <button 
            className="nav-control-btn"
            onClick={handlePauseResume}
            title={isPaused ? t('navigation.resume') : t('navigation.pause')}
          >
            {isPaused ? <Play size={20} /> : <Pause size={20} />}
          </button>
          
          <button 
            className="nav-control-btn stop"
            onClick={handleStopNavigation}
            title={t('navigation.stop')}
          >
            <Square size={20} />
          </button>
          
          <button 
            className="nav-control-btn end"
            onClick={handleEndNavigation}
            title={t('navigation.end')}
          >
            {t('navigation.end')}
          </button>
        </div>
      </div>

      {/* Current Instruction */}
      <div className="navigation-instruction">
        <div 
          className="instruction-icon"
          style={{ color: getInstructionColor(currentInstruction.type) }}
        >
          {getInstructionIcon(currentInstruction.type)}
        </div>
        <div className="instruction-content">
          <h2>{currentInstruction.instruction}</h2>
          {currentInstruction.distance > 0 && (
            <p className="instruction-distance">{currentInstruction.distance}m</p>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="navigation-map">
        <button
          type="button"
          className="map-section-info-btn"
          onClick={() => setShowMapExplainer(true)}
          aria-label={t('mapExplainer.open')}
        >
          <CircleHelp size={26} strokeWidth={2} aria-hidden />
        </button>
        <MapContainer
          center={currentPosition || routeCoordinates[0] || OSDORP_CENTER}
          zoom={16}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          maxBounds={OSDORP_BOUNDS}
          maxBoundsViscosity={1.0}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Osdorp-Midden neighborhood outline */}
          <GeoJSON
            data={wijkGeoJson as any}
            style={() => ({
              color: '#FF6B00',
              weight: 3,
              fillOpacity: 0,
              dashArray: '5, 5',
            })}
          />
          
          {/* Route */}
          <Polyline 
            positions={routeCoordinates} 
            color="#007bff" 
            weight={6} 
            opacity={0.8} 
          />
          
          {/* Start Marker */}
          {startLocation.coordinates && (
            <Marker position={[startLocation.coordinates.lat, startLocation.coordinates.lng]}>
              <Popup>{t('navigation.start')}: {startLocation.address}</Popup>
            </Marker>
          )}
          
          {/* End Marker */}
          {endLocation.coordinates && (
            <Marker position={[endLocation.coordinates.lat, endLocation.coordinates.lng]}>
              <Popup>{t('navigation.destination')}: {endLocation.address}</Popup>
            </Marker>
          )}
          
          {/* Current Position Marker - Red Dot */}
          {currentPosition && (
            <Marker 
              position={currentPosition}
              icon={currentLocationIcon}
            >
              <Popup>{t('navigation.currentLocation')}</Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      <div className="navigation-footer">
        <div className="navigation-footer-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${routeProgress.progressPercentage}%` }}
            />
          </div>
          <span className="progress-text">
            {routeProgress.progressPercentage}% {t('navigation.completed')}
          </span>
        </div>
        <div className="navigation-footer-meta">
          <span className="navigation-footer-eta">
            {t('navigation.eta')}: {routeProgress.remainingDuration} {t('unit.minutes')}
          </span>
          <span className="navigation-footer-distance">
            {routeProgress.remainingDistance} m · {isPaused ? t('navigation.paused') : t('navigation.navigating')}
          </span>
        </div>
      </div>

      {/* Route Feedback Modal */}
      <RouteFeedbackModal
        isOpen={showFeedbackModal}
        onClose={handleFeedbackClose}
        onSave={handleFeedbackSave}
        onSkip={handleFeedbackSkip}
        routeInfo={{
          startAddress: startLocation.address,
          endAddress: endLocation.address,
          date: getCurrentDate()
        }}
      />

      <MapExplainerModal
        isOpen={showMapExplainer}
        onClose={() => setShowMapExplainer(false)}
      />

      {/* Mock Geolocation Controls */}
      <div className="mock-controls-container">
        <button
          className="mock-controls-toggle"
          onClick={() => setShowMockControls(!showMockControls)}
          title="Toggle Simulation Controls"
        >
          Simulation Mode
        </button>
        
        {showMockControls && (
          <div className="mock-controls-panel">
            <div className="mock-controls-header">
              <h4>Simulation Controls</h4>
            </div>
              
              <div className="mock-controls-section">
                <div className="mock-controls-row">
                  <button
                    className={`mock-control-btn ${isMockEnabled ? 'active' : ''}`}
                    onClick={isMockEnabled ? handleDisableMock : handleEnableMock}
                  >
                    {isMockEnabled ? <MapPinOff size={16} /> : <MapPin size={16} />}
                    <span>{isMockEnabled ? 'Disable Mock' : 'Enable Mock'}</span>
                  </button>
                </div>

                {isMockEnabled && (
                  <>
                    <div className="mock-controls-row">
                      <button
                        className="mock-control-btn"
                        onClick={isSimulating ? handleStopSimulation : handleStartSimulation}
                        disabled={routeCoordinates.length === 0}
                      >
                        {isSimulating ? (
                          <>
                            <Square size={16} />
                            <span>Stop Simulation</span>
                          </>
                        ) : (
                          <>
                            <Play size={16} />
                            <span>Start Simulation</span>
                          </>
                        )}
                      </button>
                      
                      {isSimulating && (
                        <>
                          <button
                            className="mock-control-btn"
                            onClick={handlePauseSimulation}
                          >
                            <Pause size={16} />
                            <span>Pause</span>
                          </button>
                          <button
                            className="mock-control-btn"
                            onClick={handleResumeSimulation}
                          >
                            <Play size={16} />
                            <span>Resume</span>
                          </button>
                        </>
                      )}
                      
                      <button
                        className="mock-control-btn"
                        onClick={handleResetSimulation}
                        disabled={routeCoordinates.length === 0}
                      >
                        <RotateCcw size={16} />
                        <span>Reset</span>
                      </button>
                    </div>

                    <div className="mock-controls-row">
                      <label className="mock-speed-label">
                        <Gauge size={16} />
                        <span>Speed: {simulationSpeed.toFixed(1)} m/s ({(simulationSpeed * 3.6).toFixed(1)} km/h)</span>
                      </label>
                      <div className="mock-speed-buttons">
                        <button
                          className="mock-speed-btn"
                          onClick={() => handleSpeedChange(1.4)}
                          title="0.5x speed"
                        >
                          0.5x
                        </button>
                        <button
                          className="mock-speed-btn"
                          onClick={() => handleSpeedChange(2.8)}
                          title="1x speed (normal walking)"
                        >
                          1x
                        </button>
                        <button
                          className="mock-speed-btn"
                          onClick={() => handleSpeedChange(5.6)}
                          title="2x speed"
                        >
                          2x
                        </button>
                        <button
                          className="mock-speed-btn"
                          onClick={() => handleSpeedChange(10.2)}
                          title="4x speed"
                        >
                          4x
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default NavigationMode;
